import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Timeline,
  Button,
  Divider,
  Spin,
} from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  LinkedinOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { postReq } from "../api";
import AddOutreachModal from "../components/addOutreachModel";
import UpdateOutreachModal from "../components/updateOutreachModel";
import moment from "moment";

const { Title, Text } = Typography;

// 🎨 Stage colors
const stageColorMap = {
  EMAIL: "blue",
  INTRO: "blue",
  FU1: "gold",
  FU2: "orange",
  FU3: "red",
  REPLIED: "green",
};

// 📡 Channel icons
const channelIconMap = {
  EMAIL: <MailOutlined />,
  PHONE: <PhoneOutlined />,
  LINKEDIN: <LinkedinOutlined />,
};

export default function SpocOutreachSummary({
  spocId = "697cad1ec0d1df5ad7887401",
}) {
  const [loading, setLoading] = useState(true);
  const [spoc, setSpoc] = useState(null);
  const [company, setCompany] = useState(null);
  const [summary, setSummary] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedOutreach, setSelectedOutreach] = useState(null);

  const stageColorMap = {
    EMAIL: "blue",
    FU1: "gold",
    FU2: "orange",
    FU3: "red",
    REPLIED: "green",
  };
  const statusColorMap = {
    Pending: "orange",
    IN_DISCUSSION: "green",
    REPLIED: "blue",
    NOT_INTERESTED: "red",
    NO_REPLY: "gold",
  };

  const openOutreach = timeline.find((item) => item.status === "PENDING");

  // 📡 Fetch SPOC outreach summary
  const fetchData = async () => {
    setLoading(true);
    const res = await postReq(`/common/outreach/spoc/summary/${spocId}`);
    const data = await res.data;

    setSpoc(data.spoc);
    setCompany(data.company);
    setSummary(data.summary);
    setTimeline(data.outreachList || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [spocId]);

  const renderFollowUpChip = (date) => {
    if (!date) return "-";

    const diff = dayjs(date).diff(dayjs(), "day");

    if (diff < 0) return <Tag color="red">Overdue</Tag>;
    if (diff === 0) return <Tag color="gold">Today</Tag>;
    return <Tag color="blue">{diff}d to go</Tag>;
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    // <Card
    //   title={
    //     <div
    //       style={{
    //         display: "flex",
    //         justifyContent: "space-between",
    //         alignItems: "center",
    //         padding: "16px",
    //         borderRadius: "20px 20px 0px 0px",
    //         margin: "0px -24px",
    //       }}
    //     >
    //       <div>
    //         <Title
    //           level={4}
    //           style={{
    //             marginBottom: 0,
    //             fontSize: "20px",
    //             fontWeight: "800",
    //             color: "#13487D",
    //           }}
    //         >
    //           Summary
    //         </Title>
    //       </div>
    //     </div>
    //   }
    //   style={{
    //     paddingLeft: "16px",
    //     color: "#13487D",
    //     borderRadius: "18px",
    //     maxWidth: "100%",
    //     overflow: "hidden",
    //   }}
    //   bordered={false}
    // >
    <div style={{ padding: 24, margin: "0 auto" }}>
      {/* 🔹 HEADER */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={6}>
          <Title level={4} style={{ margin: 0 }}>
            {spoc.name}
          </Title>

          <Text type="secondary">
            {spoc.designation} · {company?.name}
          </Text>

          <Space>
            <Tag color={statusColorMap[spoc.status]}>{spoc.status}</Tag>
            <Text>📧 {spoc.email}</Text>
            {spoc.linkedinUrl && (
              <a href={spoc.linkedinUrl} target="_blank" rel="noreferrer">
                LinkedIn
              </a>
            )}
          </Space>
        </Space>
      </Card>

      {/* 🔹 QUICK STATS */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Text type="secondary">Total Touches</Text>
            <Title level={5}>{summary.totalTouches}</Title>
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Text type="secondary">Current Stage</Text>
            <Title level={5}>
              <Tag color={stageColorMap[summary.currentStage]}>
                {summary.currentStage}
              </Tag>
            </Title>
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Text type="secondary">Last Contacted</Text>
            <Title level={5}>
              {summary.lastContacted
                ? dayjs(summary.lastContacted).format("DD MMM YYYY")
                : "-"}
            </Title>
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Text type="secondary">Next Follow-up</Text>
            <Title level={5}>
              <div>{renderFollowUpChip(summary.nextFollowUpAt)}</div>
            </Title>
          </Card>
        </Col>
      </Row>

      {/* 🔹 ACTION BAR */}
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setOpenModal(true)}
          disabled={!!openOutreach}
        >
          Add Outreach
        </Button>
        <Button disabled={!!openOutreach}>Follow-up</Button>
        <Button danger>Not Interested</Button>
      </Space>

      <Divider />

      {/* 🔹 TIMELINE */}
      <Timeline mode="left">
        {timeline.map((item) => {
          const isOpen =
            item.status === "PENDING" || item.status === "IN_DISCUSSION";
          return (
            <Timeline.Item
              key={item._id}
              dot={channelIconMap[item.channel]}
              color={stageColorMap[item.stage]}
            >
              <Card size="small">
                <Space direction="vertical" size={4}>
                  <Space>
                    <Tag color={stageColorMap[item.stage]}>{item.stage}</Tag>
                    <Text type="secondary">
                      {dayjs(item.contactedAt).format("DD MMM YYYY, hh:mm A")}
                    </Text>
                    {isOpen && <Tag color="gold">OPEN</Tag>}
                    {
                      <Tag color={statusColorMap[item.status]}>
                        {item.status}
                      </Tag>
                    }
                  </Space>

                  <Text>
                    {item.remark && item.remark.trim()
                      ? item.remark
                      : "— No remarks —"}
                  </Text>

                  {item.nextFollowUpAt && (
                    <Tag>
                      Follow-up: {dayjs(item.nextFollowUpAt).format("DD MMM")}
                    </Tag>
                  )}
                  {isOpen && (
                    <Button
                      size="small"
                      type="link"
                      onClick={() => {
                        setSelectedOutreach(item);
                        setOpenUpdateModal(true);
                      }}
                    >
                      Update Status
                    </Button>
                  )}
                  {item.discussionNotes?.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">Discussion Notes</Text>

                      <div style={{ marginTop: 4 }}>
                        {item.discussionNotes.map((note, idx) => (
                          <Card
                            key={idx}
                            size="small"
                            style={{
                              background: "#F6F7F9",
                              marginBottom: 6,
                            }}
                          >
                            <Text>{note.note}</Text>
                            <div style={{ fontSize: 11, color: "#8A9099" }}>
                              {moment(note.createdAt).format(
                                "DD MMM YYYY, hh:mm A",
                              )}
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </Space>
              </Card>
            </Timeline.Item>
          );
        })}
      </Timeline>

      <AddOutreachModal
        open={openModal}
        spocId={spocId}
        onClose={() => setOpenModal(false)}
        onSuccess={fetchData}
      />
      <UpdateOutreachModal
        open={openUpdateModal}
        outreach={selectedOutreach}
        onClose={() => setOpenUpdateModal(false)}
        onSuccess={fetchData}
      />
    </div>

    // </Card>
  );
}
